import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

type Mode = 'choose' | 'create' | 'upload';

interface DocumentCreateOrUploadDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called when user wants to create an empty document */
  onCreate: (title: string, description: string) => Promise<void>;
  /** Called when user selects a file to upload */
  onUpload: (file: File, title: string, description: string) => Promise<void>;
  creating?: boolean;
  uploading?: boolean;
}

export function DocumentCreateOrUploadDialog({
  open,
  onClose,
  onCreate,
  onUpload,
  creating,
  uploading,
}: DocumentCreateOrUploadDialogProps) {
  const [mode, setMode] = useState<Mode>('choose');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const isLoading = creating || uploading;

  const handleClose = () => {
    setMode('choose');
    setTitle('');
    setDescription('');
    setSelectedFile(null);
    onClose();
  };

  const handleCreate = async () => {
    await onCreate(title, description);
    handleClose();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    await onUpload(selectedFile, title || selectedFile.name, description);
    handleClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!title) setTitle(file.name);
      setMode('upload');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      {mode === 'choose' && (
        <>
          <DialogTitle>Pridať dokument</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<DescriptionIcon />}
                onClick={() => setMode('create')}
                fullWidth
              >
                Vytvoriť nový dokument
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<CloudUploadIcon />}
                component="label"
                fullWidth
              >
                Nahrať súbor
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Zrušiť</Button>
          </DialogActions>
        </>
      )}

      {mode === 'create' && (
        <>
          <DialogTitle>Vytvoriť nový dokument</DialogTitle>
          <DialogContent>
            <TextField
              label="Názov"
              fullWidth
              margin="normal"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
            <TextField
              label="Popis"
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMode('choose')} disabled={isLoading}>
              Späť
            </Button>
            <Button variant="contained" onClick={handleCreate} disabled={isLoading || !title}>
              {creating ? 'Vytvára sa...' : 'Vytvoriť'}
            </Button>
          </DialogActions>
          {isLoading && <LinearProgress />}
        </>
      )}

      {mode === 'upload' && (
        <>
          <DialogTitle>Nahrať súbor</DialogTitle>
          <DialogContent>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Súbor: {selectedFile.name}
              </Typography>
            )}
            <TextField
              label="Názov"
              fullWidth
              margin="normal"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
            <TextField
              label="Popis"
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setMode('choose'); setSelectedFile(null); setTitle(''); setDescription(''); }} disabled={isLoading}>
              Späť
            </Button>
            <Button variant="contained" onClick={handleUpload} disabled={isLoading || !selectedFile || !title}>
              {uploading ? 'Nahrávanie...' : 'Nahrať'}
            </Button>
          </DialogActions>
          {isLoading && <LinearProgress />}
        </>
      )}
    </Dialog>
  );
}
